import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  MapPin, Clock, Banknote, Building2, Bookmark, BookmarkCheck,
  Wifi, ExternalLink, CheckCircle2, Eye, TrendingUp, Calendar,
  X as XIcon
} from 'lucide-react';
import { currencySymbol } from '../lib/currency';

const APPLICATION_STATUS = {
  pending:     { label: 'Applied',       color: 'bg-blue-100 text-blue-700' },
  reviewing:   { label: 'Under Review',  color: 'bg-amber-100 text-amber-700' },
  reviewed:    { label: 'Under Review',  color: 'bg-amber-100 text-amber-700' },
  shortlisted: { label: 'Shortlisted',   color: 'bg-purple-100 text-purple-700' },
  interview:   { label: 'Interview',     color: 'bg-indigo-100 text-indigo-700' },
  offered:     { label: 'Offer!',        color: 'bg-emerald-100 text-emerald-700' },
  hired:       { label: 'Offer!',        color: 'bg-emerald-100 text-emerald-700' },
  rejected:    { label: 'Not Selected',  color: 'bg-red-100 text-red-700' },
  withdrawn:   { label: 'Withdrawn',     color: 'bg-gray-100 text-gray-500' },
};

export const JobCard = ({
  job,
  showApplyButton = true,
  appliedStatus = null,   // {status, application_id} or null
  onQuickApply = null,    // callback(job) — triggers inline apply modal on search page
  onBookmark = null,      // callback(job, isSaved)
  isSaved = false,
}) => {
  const formatSalary = (min, max, currency, period) => {
    if (!min && !max) return 'Competitive';
    const symbol = currencySymbol(currency);
    const fmt = (n) => n >= 1000 ? `${symbol}${(n / 1000).toFixed(0)}k` : `${symbol}${n}`;
    const per = period === 'month' ? '/month' : '/year';
    if (min && max) return `${fmt(min)} – ${fmt(max)} ${per}`;
    if (min) return `From ${fmt(min)} ${per}`;
    return `Up to ${fmt(max)} ${per}`;
  };

  const getJobTypeLabel = (type) => ({
    full_time: 'Full Time', part_time: 'Part Time',
    contract: 'Contract', internship: 'Internship',
  }[type] || type);

  const getTimeAgo = (dateString) => {
    const diffDays = Math.floor((Date.now() - new Date(dateString)) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  const appInfo = appliedStatus ? APPLICATION_STATUS[appliedStatus.status] || APPLICATION_STATUS.pending : null;
  const isApplied = !!appliedStatus;
  const isClosedForUser = isApplied && ['rejected', 'withdrawn'].includes(appliedStatus?.status);

  return (
    <Card
      className={`group bg-white hover:shadow-lg transition-all duration-300 ${isApplied ? 'border-l-4 border-l-[#003366]' : 'hover:border-[#003366]/30'}`}
      data-testid={`job-card-${job.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Company Logo */}
          <div className="w-14 h-14 rounded-xl bg-[#F0F4F8] flex items-center justify-center flex-shrink-0 group-hover:bg-[#003366]/10 transition-colors">
            {job.company_logo ? (
              <img src={job.company_logo} alt={job.company_name} className="w-10 h-10 object-contain rounded" />
            ) : (
              <Building2 className="w-7 h-7 text-[#003366]" />
            )}
          </div>

          {/* Job Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/jobs/${job.id}`} className="block" data-testid={`job-link-${job.id}`}>
                  <h3 className="font-semibold text-lg text-foreground group-hover:text-[#003366] transition-colors line-clamp-1">
                    {job.title}
                  </h3>
                </Link>
                <p className="text-muted-foreground text-sm mt-0.5">{job.company_name}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Applied status badge */}
                {appInfo && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${appInfo.color}`}>
                    {appInfo.label}
                  </span>
                )}
                {/* Bookmark */}
                <button
                  className="p-2 rounded-lg hover:bg-secondary transition-colors"
                  onClick={() => onBookmark?.(job, !isSaved)}
                  title={isSaved ? 'Remove bookmark' : 'Save job'}
                  data-testid={`bookmark-job-${job.id}`}
                >
                  {isSaved
                    ? <BookmarkCheck className="w-5 h-5 text-[#003366]" />
                    : <Bookmark className="w-5 h-5 text-muted-foreground" />
                  }
                </button>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="secondary" className="font-medium">
                {getJobTypeLabel(job.job_type)}
              </Badge>
              {job.remote && (
                <Badge variant="outline" className="font-medium text-emerald-600 border-emerald-200 bg-emerald-50">
                  <Wifi className="w-3 h-3 mr-1" />Remote
                </Badge>
              )}
              {job.is_featured && (
                <Badge className="bg-amber-100 text-amber-700 border-0 font-medium">Featured</Badge>
              )}
              {job.skills_required?.slice(0, 2).map((skill, idx) => (
                <Badge key={idx} variant="outline" className="font-normal">{skill}</Badge>
              ))}
              {job.skills_required?.length > 2 && (
                <span className="text-xs text-muted-foreground">+{job.skills_required.length - 2} more</span>
              )}
            </div>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{job.location}</span>
              <span className="flex items-center gap-1.5"><Banknote className="w-4 h-4" />{formatSalary(job.salary_min, job.salary_max, job.currency, job.salary_period)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{getTimeAgo(job.created_at)}</span>
              {job.applications_count > 0 && (
                <span className="flex items-center gap-1.5 text-xs">
                  {job.applications_count} applicant{job.applications_count !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Application status timeline bar (when applied) */}
            {isApplied && !isClosedForUser && (
              <div className="mt-3 pt-3 border-t">
                <div className="flex items-center gap-1">
                  {['pending', 'reviewed', 'shortlisted', 'interview', 'hired'].map((stage, i, arr) => {
                    const stageOrder = { pending: 0, reviewing: 1, reviewed: 1, shortlisted: 2, interview: 3, offered: 4, hired: 4 };
                    const currentOrder = stageOrder[appliedStatus.status] ?? 0;
                    const stageO = stageOrder[stage] ?? i;
                    const isActive = stageO === currentOrder;
                    const isPast = stageO < currentOrder;
                    return (
                      <React.Fragment key={stage}>
                        <div className={`flex-1 h-1.5 rounded-full ${isPast || isActive ? 'bg-[#003366]' : 'bg-gray-200'}`} />
                        {i < arr.length - 1 && <div className="w-1" />}
                      </React.Fragment>
                    );
                  })}
                </div>
                <p className="text-xs text-[#003366] font-medium mt-1">
                  {APPLICATION_STATUS[appliedStatus.status]?.label || 'Applied'} — <Link to="/my-applications" className="underline">track application</Link>
                </p>
              </div>
            )}
            {isApplied && isClosedForUser && (
              <div className="mt-2 text-xs text-muted-foreground pt-2 border-t">
                Application {appliedStatus.status}. <Link to="/my-applications" className="underline text-[#003366]">View details</Link>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {showApplyButton && (
          <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t">
            <Link to={`/jobs/${job.id}`}>
              <Button variant="outline" size="sm" className="rounded-full" data-testid={`view-job-${job.id}`}>
                View Details <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
            {isApplied ? (
              <Link to="/my-applications">
                <Button size="sm" variant="outline" className="rounded-full border-[#003366] text-[#003366]">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  {APPLICATION_STATUS[appliedStatus.status]?.label || 'Applied'}
                </Button>
              </Link>
            ) : (
              <Button
                size="sm"
                className="bg-[#E11D48] hover:bg-[#E11D48]/90 text-white rounded-full"
                onClick={() => onQuickApply ? onQuickApply(job) : null}
                data-testid={`apply-job-${job.id}`}
                asChild={!onQuickApply}
              >
                {onQuickApply ? (
                  <span>Quick Apply</span>
                ) : (
                  <Link to={`/jobs/${job.id}?apply=true`}>Apply Now</Link>
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobCard;
